import { useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { formatNumber } from "src/helpers/Dashboard";
import WidgetPlaceholder from "./WidgetPlaceholder";
import KpiComparison, { type KpiComparisonData } from "./KpiComparison";
import KpiExportMenu from "./KpiExportMenu";
import WidgetExportCard from "./WidgetExportCard";
import type { KpiExportData } from "src/helpers/widgetExport";

// ─── Types ────────────────────────────────────────────────────────────────────

// One point on the trend sparkline. `numerator`/`denominator` back a
// "X of Y <label>" tooltip (e.g. PA Approval: "461 of 758 PA determinations");
// `covered_count`/`referral_count` back a "X of Y referrals" tooltip used by
// other sparkle KPIs that share this same component. A point only ever
// carries one of the two pairs, never both.
//
// Nullable rather than merely optional: a count-type spark sends
// `"denominator": null` explicitly instead of omitting the key.
type SparkPoint = {
  x: string;
  y: number;
  numerator?: number | null;
  denominator?: number | null;
  covered_count?: number | null;
  referral_count?: number | null;
  label?: string;
};

// One renderable KPI — value, comparison chip and sparkline. A flat payload
// *is* one of these; a segmented payload holds several of them side by side
// (see parseSparklePayload).
type SparkleSegment = {
  label?: string;
  total_count?: number | string;
  subtitle?: string;
  formula?: string;
  comparison?: KpiComparisonData | null;
  // Backend-supplied line/value color, e.g. "#16a34a" — falls back to
  // DEFAULT_SPARK_COLOR when absent.
  color?: string;
  spark?: {
    period?: string;
    value_type?: string;
    points?: SparkPoint[];
  };
};

type SparkleWidgetProps = {
  // Backend-declared download capability, surfaced by WidgetRenderer
  // from the raw response (siblings of `.data`) and handed to the
  // export menu, which gates "Download Details" on it.
  hasDownloadOptions?: boolean;
  downloadOptions?: { csv?: boolean; case_data?: boolean };
  isLoading: boolean;
  data: unknown;
  title?: string;
  description?: string | null;
  widgetId?: string | number;
  // Forwarded to KpiExportMenu: appliedFilters puts the "Applied Filters"
  // block at the top of the Excel/Case List downloads, and dashboardName is
  // what the backend download items need. Without these the sparkle exports
  // came out with no record of which filters produced them.
  dashboardName?: string;
  appliedFilters?: Record<string, unknown>;
  // Rendered off-screen at a fixed size for image/PDF export.
  exportMode?: boolean;
  // Builder-saved display prefs (charts/widgetConfig.ts): which tab to open
  // on, and whether to start with the sparkline collapsed.
  defaultTab?: string;
  defaultHideChart?: boolean;
  // Which tab a segmented payload should open on. Only ever set by the export
  // path: the off-screen export view mounts a *fresh* SparkleWidget, so
  // without this a PNG/PDF taken from the "Precert" tab would silently
  // snapshot the first tab instead.
  segmentKey?: string;
};

// Original hardcoded color, kept as the fallback for payloads that don't
// send their own `color`.
const DEFAULT_SPARK_COLOR = "#0d6efd";

// ─── Payload shapes ───────────────────────────────────────────────────────────
//
// `data` arrives as `unknown` from the dashboard API in one of two shapes:
//
//   flat       `data` IS the KPI — { total_count, comparison, spark, ... }
//   segmented  `data` holds several named KPIs side by side, plus the
//              `label`/`formula` they share:
//              { label, formula, all: {…}, medical_precert: {…}, pharmacy_pa: {…} }
//
// The segmented shape is what renders the tab strip. Its keys are
// backend-defined per widget and vary (there may be two tabs or four), so
// they can't be hardcoded: a top-level value is taken as a segment when it's
// a plain object carrying a `total_count` or its own nested `spark` — the two
// things this widget actually renders. That test is what keeps the flat shape
// working untouched, since none of a flat payload's own children pass it
// (`spark` has no total_count and no nested `spark`; `comparison` has
// neither; `case_ids`/`case_list` are arrays). No segments found → flat, and
// the widget renders exactly as it did before tabs existed.

type SparkleSegmentEntry = {
  // The payload key ("medical_precert"). Identifies the tab across re-renders.
  key: string;
  // Tab text — the backend's own `label` ("Precert"), falling back to the
  // key so a segment shipped without one still gets a readable tab.
  label: string;
  segment: SparkleSegment;
};

type ParsedSparkle = {
  // Top-level fields that aren't segments. For a flat payload that's the
  // whole thing (and `segments` is empty); for a segmented one it's
  // `label`/`formula` and anything else the backend puts alongside the
  // segments — but never the segments themselves.
  root: Record<string, unknown>;
  segments: SparkleSegmentEntry[];
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isSegment = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value) &&
  ("total_count" in value || isPlainObject(value["spark"]));

// The one place `unknown` becomes SparkleSegment. Every field is read
// defensively downstream (optional chaining + fallbacks), so this structural
// narrow stands in for a full zod parse, matching the sibling widgets in this
// folder (CustomBarWidget, SentimentSplitWidget, …).
const asSegment = (value: Record<string, unknown>): SparkleSegment =>
  value as SparkleSegment;

// Tab text for segments whose display name is fixed by product rather than
// taken from the payload. Checked *before* the backend's own `label`, so the
// tab wording can't drift if that field changes; the payload keys themselves
// ("medical_precert", "pharmacy_pa") read as internal names and are not what
// should be shown. Any segment not listed here still falls back to its
// `label` and then to its key, so adding a segment needs no change in this
// map to get a readable tab.
const TAB_LABELS: Record<string, string> = {
  medical_precert: "Med Precert",
  pharmacy_pa: "Rx PA",
};

// Last-resort tab text, for a segment that is neither in TAB_LABELS nor
// carries a `label`: "specialty_authorization" → "Specialty Authorization".
// Acronyms are re-uppercased the same way the case-list export headers do it
// (caseListHeader in widgetExport.ts), so a future key like "specialty_pa"
// reads as "Specialty PA" rather than "Specialty Pa".
const keyToLabel = (key: string): string =>
  key
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(/\b(Ids?|Npi|Pa)\b/g, (match) => match.toUpperCase());

export const parseSparklePayload = (raw: unknown): ParsedSparkle => {
  if (!isPlainObject(raw)) return { root: {}, segments: [] };

  const root: Record<string, unknown> = {};
  const segments: SparkleSegmentEntry[] = [];

  // Key order is the backend's declared tab order — JS preserves insertion
  // order for string keys, so "all" stays first.
  Object.entries(raw).forEach(([key, value]) => {
    if (!isSegment(value)) {
      root[key] = value;
      return;
    }
    const segment = asSegment(value);
    const label = TAB_LABELS[key] ?? segment.label?.trim();
    segments.push({ key, label: label || keyToLabel(key), segment });
  });

  return { root, segments };
};

// ─── Export payload ───────────────────────────────────────────────────────────

// "PA Approval" (root) + "Medical Precert" (tab) → downloads land as
// "pa-approval-medical-precert.xlsx" rather than a bare one naming only the
// tab or only the KPI, so a file
// names both the KPI and the tab it came from.
const exportLabel = (
  root: Record<string, unknown>,
  active: SparkleSegmentEntry,
): string => {
  const rootLabel =
    typeof root["label"] === "string" ? root["label"].trim() : "";
  if (!rootLabel || rootLabel === active.label) return active.label;
  return `${rootLabel} — ${active.label}`;
};

// What every download describes: the tab that's open. The active segment's
// own total_count/comparison/spark/case_list, plus the shared root fields a
// segmented payload keeps *outside* its segments — the export filename, the
// xlsx sheet name and the PDF footer all read `label`/`formula`, which only
// exist at the root. The sibling segments were already left out by
// parseSparklePayload (they're other tabs' data, not this download's), and
// the segment's own fields win over the root's.
//
// Flat payload: there's no active segment and `root` is the whole payload, so
// this hands back exactly what the downloads got before tabs existed.
const buildExportData = (
  parsed: ParsedSparkle,
  active: SparkleSegmentEntry | undefined,
): KpiExportData => {
  const merged = active
    ? {
        ...parsed.root,
        ...active.segment,
        label: exportLabel(parsed.root, active),
      }
    : parsed.root;

  // KpiExportData declares narrow types for fields this widget never reads
  // (case_ids, case_list, value, …); arriving from an `unknown` payload those
  // are `unknown` here, so the two can't be reconciled structurally without
  // re-validating keys nothing in this file touches. What makes the cast safe
  // is that widgetExport.ts guards every one of them at use (Array.isArray /
  // isScalar / isId) rather than trusting the declared type.
  return merged as KpiExportData;
};

// ─── Formatting ───────────────────────────────────────────────────────────────

// "2026-W33" → "W33", "2026-08-10" → "Aug 10". Anything else (a label the
// backend already formatted, e.g. "10 Aug.") passes through unchanged.
const formatPeriodLabel = (raw: string): string => {
  const week = /^\d{4}-W(\d+)$/i.exec(raw);
  if (week) return `W${week[1]}`;

  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (day) {
    const [, y, m, d] = day;
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(
      "en-US",
      { month: "short", day: "numeric" },
    );
  }

  return raw;
};

// ─── Chart ────────────────────────────────────────────────────────────────────

// Carried on each Highcharts point so the tooltip formatter can read the raw
// x label and underlying counts — the x-axis labels themselves are hidden on
// this compact sparkline (see xAxis.labels below), so the tooltip is the only
// place this detail is ever shown.
type SparkPointMeta = Pick<
  SparkPoint,
  | "x"
  | "numerator"
  | "denominator"
  | "covered_count"
  | "referral_count"
  | "label"
>;

const toSeriesData = (
  points: SparkPoint[],
): Array<{ y: number; custom: SparkPointMeta }> =>
  points.map((p) => ({
    y: p.y,
    custom: {
      x: p.x,
      numerator: p.numerator,
      denominator: p.denominator,
      covered_count: p.covered_count,
      referral_count: p.referral_count,
      label: p.label,
    },
  }));

// Numbers inside the tooltip are always this dark navy, regardless of the
// series' own accent color, so they stay readable against the light tooltip
// background even when `color` is something pale.
const boldNumber = (n: number): string =>
  `<b style="color:#0f172a;">${formatNumber(n)}</b>`;

// The "X of Y ..." ratio line under the value — whichever shape this point
// supports, or "" if neither pair is present.
const formatTooltipRatio = (meta: SparkPointMeta): string => {
  if (meta.numerator != null && meta.denominator != null) {
    const suffix = meta.label ? ` ${meta.label}` : "";
    return `${boldNumber(meta.numerator)} of ${boldNumber(meta.denominator)}${suffix}`;
  }
  // Numerator with no denominator: nothing to build "X of Y" from, so only
  // the label (if any) is shown. Matches this payload shape as it's been
  // seen in practice — not treated as an error case.
  if (meta.numerator != null) {
    return meta.label ?? "";
  }
  if (meta.covered_count != null && meta.referral_count != null) {
    return `${boldNumber(meta.covered_count)} of ${boldNumber(meta.referral_count)} referrals`;
  }
  return "";
};

// This Highcharts version's own .d.ts types tooltip.formatter's `this` as a
// plain `Point` — but the object Highcharts actually calls it with (and the
// only thing this widget needs from it) is this shape, including the
// `custom` bag toSeriesData attaches to each point. One boundary cast below
// bridges that known mismatch, narrowed to exactly these fields rather than
// left as the wider `unknown` the mismatch would otherwise force.
type TooltipFormatterThis = {
  y?: number;
  point?: {
    category?: string | number;
    options?: { custom?: SparkPointMeta };
  };
};

function formatTooltip(
  this: TooltipFormatterThis,
  isPercent: boolean,
  color: string,
): string {
  const meta = this.point?.options?.custom;
  const dateLabel = meta?.x ?? this.point?.category ?? "";
  const value = isPercent ? `${this.y ?? 0}%` : formatNumber(this.y ?? 0);
  const ratio = meta ? formatTooltipRatio(meta) : "";

  return `<div style="padding:3px 4px;min-width:140px;">
    <div style="font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:3px;">${dateLabel}</div>
    <div style="font-size:16px;font-weight:700;color:${color};line-height:1;">${value}</div>
    ${ratio ? `<div style="font-size:11px;color:#64748b;margin-top:4px;">${ratio}</div>` : ""}
  </div>`;
}

function buildChartOptions(
  points: SparkPoint[],
  valueType: string | undefined,
  exportMode: boolean | undefined,
  color: string,
): Highcharts.Options {
  const isPercent = (valueType ?? "").toLowerCase().startsWith("percent");

  return {
    chart: {
      type: "areaspline",
      height: exportMode ? 220 : 160,
      // No animation for export so the snapshot captures the final frame.
      animation: exportMode ? false : undefined,
      spacing: [10, 10, 10, 10],
      style: { fontFamily: "Outfit, sans-serif" },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories: points.map((p) => formatPeriodLabel(p.x)),
      labels: { enabled: false },
      title: {
        text: "Weekly Data",
        style: { fontSize: "11px", fontWeight: "600", color: "#94a3b8" },
      },
      lineColor: "#dee2e6",
      tickLength: 0,
    },
    yAxis: {
      title: { text: undefined },
      labels: { enabled: false },
      gridLineWidth: 0,
    },
    tooltip: {
      useHTML: true,
      formatter() {
        return formatTooltip.call(
          this as unknown as TooltipFormatterThis,
          isPercent,
          color,
        );
      },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      padding: 8,
      style: { fontSize: "12px" },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.15,
        lineWidth: 2.5,
        marker: {
          enabled: true,
          radius: 4,
          symbol: "circle",
          fillColor: "#fff",
          lineWidth: 2,
          // Load-bearing `undefined` — do not delete as redundant. Highcharts
          // ships marker.lineColor: "#ffffff" (SeriesDefaults.js), so leaving
          // this out gives a white ring on a white fill and the points read as
          // blank. Explicitly undefined is documented as "the series' or
          // point's color is used", which is what makes the ring the series
          // color. states.hover.lineColor inherits it, so this fixes the
          // hovered marker too.
          lineColor: undefined,
        },
        states: { hover: { lineWidthPlus: 0 } },
      },
    },
    series: [
      {
        type: "areaspline",
        name: "Cases",
        data: toSeriesData(points),
        color,
        // Skip the draw-in animation for export so the snapshot isn't mid-frame.
        animation: exportMode ? false : undefined,
      },
    ],
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SparkleWidget({
  isLoading,
  data,
  title,
  description,
  widgetId,
  dashboardName,
  appliedFilters,
  hasDownloadOptions,
  downloadOptions,
  exportMode,
  segmentKey,
  defaultTab,
  defaultHideChart,
}: SparkleWidgetProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  // Live on-screen toggle from the KpiExportMenu context menu. Export
  // snapshots always mount a fresh SparkleWidget instance (see renderExport
  // below), which defaults this back to false, so exports always include
  // the chart regardless of what's hidden on screen.
  // exportMode is excluded on purpose: a snapshot must always contain the
  // chart, so a saved "hide line chart" must not follow the widget into the
  // off-screen export mount.
  const [isChartHidden, setIsChartHidden] = useState(
    exportMode ? false : defaultHideChart === true,
  );
  // The open tab, tracked by payload key rather than array index so a filter
  // change that reorders or renames the segments can't leave the selection
  // pointing at a different KPI than the one that was clicked. null means
  // "nothing picked yet" and resolves to the first segment below, which is
  // why no effect is needed to seed or re-validate this.
  // segmentKey (the export path) wins over the saved default so a PNG taken
  // from a given tab still snapshots that tab.
  const [activeKey, setActiveKey] = useState<string | null>(
    segmentKey ?? defaultTab ?? null,
  );

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={200} />;
  }

  const { root, segments } = parseSparklePayload(data);

  // A key that's no longer in the payload falls back to the first segment —
  // the same path as the initial activeKey === null render. Stays undefined
  // for a flat payload, which is what switches the tab strip off.
  const activeEntry = segments.find((s) => s.key === activeKey) ?? segments[0];
  // Flat payload: the root IS the KPI.
  const sparkle = activeEntry?.segment ?? asSegment(root);
  const points = sparkle.spark?.points ?? [];
  const color = sparkle.color || DEFAULT_SPARK_COLOR;

  const body = (
    <>
      {segments.length > 0 && (
        <div
          className="sparkle-widget__tabs"
          role="tablist"
          aria-label={
            typeof root["label"] === "string" ? root["label"] : (title ?? "")
          }
        >
          {segments.map((entry) => {
            const isActive = entry.key === activeEntry?.key;
            return (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`sparkle-widget__tab${
                  isActive ? " sparkle-widget__tab--active" : ""
                }`}
                onClick={() => setActiveKey(entry.key)}
              >
                {entry.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="kpi-counter-widget d-flex flex-column gap-2 align-items-center text-center flex-fill justify-content-center">
        <p className="kpi-counter-widget__value mb-1">
          {formatNumber(sparkle.total_count ?? 0)}
        </p>
        {sparkle.subtitle && (
          <p
            className="mb-1"
            style={{ fontSize: 11.5, color: "var(--bs-secondary-color)" }}
          >
            {sparkle.subtitle}
          </p>
        )}
        <KpiComparison comparison={sparkle.comparison} />
      </div>

      {!isChartHidden && points.length > 0 && (
        <div className="sparkle-widget__chart">
          <HighchartsReact
            highcharts={Highcharts}
            options={buildChartOptions(
              points,
              sparkle.spark?.value_type,
              exportMode,
              color,
            )}
          />
        </div>
      )}
    </>
  );

  if (exportMode) {
    return <div className="sparkle-widget">{body}</div>;
  }

  return (
    <div
      ref={hostRef}
      className="sparkle-widget d-flex flex-column flex-fill"
      style={{ minHeight: 0, overflowY: "auto" }}
    >
      <KpiExportMenu
        data={buildExportData({ root, segments }, activeEntry)}
        title={title}
        description={description}
        targetRef={hostRef}
        menuId={widgetId !== undefined ? String(widgetId) : undefined}
        // Segment key, not label — only set when the payload is tabbed, so a
        // flat sparkle (and KpiWidget) send no `tab` at all.
        activeTabKey={segments.length > 0 ? activeEntry?.key : undefined}
        dashboardName={dashboardName}
        appliedFilters={appliedFilters}
        hasDownloadOptions={hasDownloadOptions}
        downloadOptions={downloadOptions}
        renderExport={() => (
          <WidgetExportCard title={title}>
            {/* The raw payload is handed over untouched and re-parsed by the
                fresh instance; segmentKey is what keeps the snapshot on the
                tab the user is actually looking at. */}
            <SparkleWidget
              isLoading={false}
              data={data}
              exportMode
              segmentKey={activeEntry?.key}
            />
          </WidgetExportCard>
        )}
        isChartHidden={isChartHidden}
        onToggleChart={() => setIsChartHidden((hidden) => !hidden)}
      />
      {body}
    </div>
  );
}
